import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { HiCheckCircle, HiArrowRight } from 'react-icons/hi';
import { useCurrency } from '../context/CurrencyContext';

const API =  process.env.REACT_APP_API_URL;

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { formatCurrency } = useCurrency();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setError('No order ID provided.');
      return;
    }

    // 🔥 Force no Authorization header to bypass JWT filter
    axios.get(`${API}/api/orders/${orderId}`, {
      headers: { Authorization: '' }   // override any global interceptor
    })
      .then(res => {
        if (res.data) {
          setOrder(res.data);
        } else {
          setError('Order not found.');
        }
      })
      .catch(err => {
        console.error('Order fetch error:', err);
        setError('Unable to load order. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/uploads/')) return `${API}/images/${url.replace('/uploads/', '')}`;
    return url;
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin h-10 w-10 border-4 border-black border-t-transparent rounded-full"></div>
    </div>
  );

  if (error || !order) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          {error ? 'Oops!' : 'Order not found'}
        </h2>
        <p className="text-gray-500 mb-6">
          {error || "We couldn't locate your order. It may still be processing."}
        </p>
        <Link to="/products" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-900 transition">
          Continue Shopping <HiArrowRight />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-2xl">
        {/* Banner */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <HiCheckCircle className="text-4xl text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {order.status === 'PAID' ? 'Order Confirmed!' : 'Order Received!'}
          </h1>
          <p className="text-gray-500 mt-2">
            {order.status === 'PAID'
              ? 'Thank you for your purchase. Your order has been placed successfully.'
              : 'Your order is being processed. We will confirm shortly.'}
          </p>
        </div>

        {/* Order card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-gray-100">
            <div>
              <p className="text-sm text-gray-500">Order Number</p>
              <p className="text-lg font-mono font-bold text-gray-900">
                #{order.id?.substring(0, 8).toUpperCase() || orderId?.substring(0, 8).toUpperCase()}
              </p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              order.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {order.status}
            </span>
          </div>

          {/* Items */}
          <div className="pt-6 space-y-5">
            <h3 className="text-lg font-semibold text-gray-900">Items</h3>
            {order.items?.map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.image ? (
                    <img src={getImageUrl(item.image)} alt={item.productName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 font-semibold text-sm">
                      {item.productName?.charAt(0).toUpperCase() || 'P'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{item.productName}</p>
                  <p className="text-sm text-gray-500">
                    {item.size && `Size: ${item.size}`}
                    {item.color && ` / ${item.color}`}
                    {item.quantity && ` · Qty: ${item.quantity}`}
                  </p>
                </div>
                <p className="font-medium text-gray-900">
                  {formatCurrency(item.price * item.quantity)}
                </p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="pt-6 mt-6 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="text-gray-900">{formatCurrency(order.subtotal || order.total)}</span>
            </div>
            {order.shippingCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Shipping</span>
                <span className="text-gray-900">{formatCurrency(order.shippingCost)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-{formatCurrency(order.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
              <span>Total</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/orders" className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-900 px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50 transition">
            View Orders
          </Link>
          <Link to="/products" className="inline-flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-900 transition">
            Continue Shopping <HiArrowRight />
          </Link>
        </div>
      </div>
    </div>
  );
}